import React from "react";
import {
  defineBlock,
  smartProps,
  useEditor,
  useToolbar,
  useSmartProp,
  useInspectorControl,
  EditableImg,
} from "@smartfire/gutenberg-react";
import { MediaUpload, MediaUploadCheck } from "@wordpress/block-editor";
import { ToolbarButton } from "@wordpress/components";
import EditableText from "../shared/editable-text";
import style from "./block.module.scss";
import { PanelBody, PanelRow, SelectControl } from "@wordpress/components";
import { useTranslation } from "react-i18next";

const ALLOWED_MEDIA_TYPES = ["image"];

function Inspector() {
  const [filter, setFilter] = useSmartProp("filter");
  const [colorTiret, setColorTiret] = useSmartProp("colorTiret");

  return (
    <>
      <PanelBody title="Configuration affichage" initialOpen={true}>
        <PanelRow>
          <SelectControl
            label="Filtre"
            options={[
              { label: "Bleu", value: "blue" },
              { label: "Sombre", value: "dark" },
            ]}
            value={filter}
            onChange={(value) => setFilter(value)}
          />
        </PanelRow>
        <PanelRow>
          <SelectControl
            label="Couleur tiret"
            options={[
              { label: "Vert", value: style.greenBouton },
              { label: "Bleu", value: style.blue },
              { label: "Orange", value: style.darkOrange },
              { label: "Gris", value: style.grey },
            ]}
            value={colorTiret}
            onChange={(value) => setColorTiret(value)}
          />
        </PanelRow>
      </PanelBody>
    </>
  );
}

function Block({
  title1,
  title2,
  text1,
  text2,
  filter,
  background,
  logo,
  colorTiret,
  breadcrumb1,
  breadcrumb1url,
  breadcrumb2,
  breadcrumb2url,
  breadcrumb3,
  breadcrumb3url,
}) {
  const { isEditing, updateData } = useEditor();
  useInspectorControl(Inspector);
  const { t, i18n } = useTranslation();

  useToolbar(() => {
    return (
      <MediaUploadCheck>
        <MediaUpload
          onSelect={(image) => updateData({ background: image })}
          allowedTypes={ALLOWED_MEDIA_TYPES}
          value={background ? background.id : null}
          render={({ open }) => (
            <ToolbarButton
              onClick={open}
              icon="camera"
              label="Modifier l'image"
            />
          )}
        />
      </MediaUploadCheck>
    );
  }, [background, updateData]);

  return (
    <div
      id="page-header"
      className={filter === "blue" ? style.blockBlue : style.blockDark}
    >
      <div
        className={style.background}
        style={{ backgroundImage: `url(${background.url})` }}
      />
      <div className={style.content}>
        {isEditing ? (
          <div className={style.breadcrumbAdmin}>
            <div className={style.firstPage}>{t("Accueil")}</div>
            <div className={style.subPage}>
              <EditableText
                className={style.page}
                smartProp="breadcrumb1"
                placeholder="Sous-page"
              />
              <div> | Lien : </div>
              <EditableText
                className={style.page}
                smartProp="breadcrumb1url"
                placeholder="Lien"
              />
            </div>
            <div className={style.subPage}>
              <EditableText
                className={style.page}
                smartProp="breadcrumb2"
                placeholder="Sous-page"
              />
              <div> | Lien : </div>
              <EditableText
                className={style.page}
                smartProp="breadcrumb2url"
                placeholder="Lien"
              />
            </div>
            <div className={style.subPage}>
              <EditableText
                className={style.page}
                smartProp="breadcrumb3"
                placeholder="Sous-page"
              />
              <div> | Lien : </div>
              <EditableText
                className={style.page}
                smartProp="breadcrumb3url"
                placeholder="Lien"
              />
            </div>
          </div>
        ) : (
          <div className={style.breadcrumb}>
            <div className={style.firstPage}>
              <a href={i18n.language === "fr" ? "/" : "/?lang=en"}>
                {t("Accueil")}
              </a>
            </div>
            {breadcrumb1 && (
              <div className={style.subPage}>
                {breadcrumb1url ? (
                  <a href={breadcrumb1url}>{breadcrumb1}</a>
                ) : (
                  <span>{breadcrumb1}</span>
                )}
              </div>
            )}
            {breadcrumb2 && (
              <div className={style.subPage}>
                {breadcrumb2url ? (
                  <a href={breadcrumb2url}>{breadcrumb2}</a>
                ) : (
                  <span>{breadcrumb2}</span>
                )}
              </div>
            )}
            {breadcrumb3 && (
              <div className={style.subPage}>
                {breadcrumb3url ? (
                  <a href={breadcrumb3url}>{breadcrumb3}</a>
                ) : (
                  <span>{breadcrumb3}</span>
                )}
              </div>
            )}
          </div>
        )}

        <EditableText
          className={style.title1}
          tagName="h1"
          smartProp="title1"
          placeholder="Titre"
        />
        <EditableText
          className={style.text1}
          smartProp="text1"
          placeholder="Texte"
        />
        <div className={style.mainPanel}>
          <div className={style.row}>
            <EditableText
              className={style.title2}
              tagName="h2"
              smartProp="title2"
              placeholder="Sous-titre"
            />
            {(isEditing || (!isEditing && logo)) && (
              <EditableImg smartProp="logo" className={style.logo} />
            )}
          </div>
          <div
            className={style.tiret}
            style={{ backgroundColor: colorTiret }}
          />
          <EditableText
            className={style.text2}
            smartProp="text2"
            placeholder="Sous-texte"
          />
        </div>
      </div>
    </div>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    title1: smartProps.string.withDefault(""),
    title2: smartProps.string.withDefault(""),
    text1: smartProps.string.withDefault(""),
    text2: smartProps.string.withDefault(""),
    filter: smartProps.string.withDefault("blue"),
    colorTiret: smartProps.string.withDefault("#94AC44"),
    background: smartProps.any.withDefault(""),
    logo: smartProps.any.withDefault(""),
    breadcrumb1: smartProps.string.withDefault(""),
    breadcrumb1url: smartProps.string.withDefault(""),
    breadcrumb2: smartProps.string.withDefault(""),
    breadcrumb2url: smartProps.string.withDefault(""),
    breadcrumb3: smartProps.string.withDefault(""),
    breadcrumb3url: smartProps.string.withDefault(""),
  },
  setup: {
    title: "Page header",
    parents: [],
  },
});
